import React, { useEffect, useState, useRef } from "react";
import { DoubleArrow } from "@mui/icons-material";
import {
  Typography,
  Box,
  IconButton,
  Paper,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  doubleArrowStyle,
  parentBoxStyle,
  childBoxStyleSpaceBetween,
  scrollTop,
  themes,
  infoStyle,
  tableRowSize,
  stickyHeader,
} from "services/constants";

import { useSelector } from "react-redux";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { templateLogHeader } from "pages/HR/Headers";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { SalaryRevisionHistory } from "./SalaryRevisionHistory";
import { tooltipStyleProps } from "services/constants/Styles";

export const ViewUserTemplateScreen = ({ onClose, userData }) => {
  const { domain, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const { result: templateData, loading } = useFetch(
    `${domain}hrzone/salary/salary_template/users-template/?user_id=${userData?.user_id}&company_id=${topbarCompanyId}`,
  );
  const containerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showSalaryRevisionHistory, setShowSalaryRevisionHistory] =
    useState(false);
  const [getRevisionId, setGetRevisionId] = useState([]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <Box sx={parentBoxStyle}>
        <Paper elevation={0} sx={{ borderRadius: 0 }}>
          <Box>
            <Box sx={childBoxStyleSpaceBetween}>
              <IconButton onClick={onClose} sx={doubleArrowStyle}>
                <DoubleArrow />
              </IconButton>
            </Box>

            <Box sx={{ ml: 2.2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {"Assignment"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View your predefined template structure that can be applied to
                employees.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <UserDetailsRow
              userDetails={{
                image_url: templateData?.employee?.image_url,
                employee_id: templateData?.employee?.employee_id,
                user_name: templateData?.employee?.employee_name,
                department_name: templateData?.employee?.department,
                designation: templateData?.employee?.designation,
              }}
            />
          </Box>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              overflow: "hidden",
              width: "100%",
            }}
          >
            <Box
              sx={{
                bgcolor: "#f5f6fb",
                px: 2,
                py: 1,
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                Current Assignment
              </Typography>
            </Box>

            <Box sx={{ p: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  bgcolor: themes?.primary,
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  p: 2,
                }}
              >
                <Stack spacing={0.5}>
                  <Typography {...infoStyle}>Salary Template</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <DescriptionIcon
                      sx={{ color: themes?.stepActiveColor, fontSize: 18 }}
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      {templateData?.current_assignment?.template_name ?? "-"}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack spacing={0.5} textAlign="right">
                  <Typography {...infoStyle}>Effective from</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarMonthIcon
                      sx={{ color: themes?.stepActiveColor, fontSize: 18 }}
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      {templateData?.current_assignment?.effective_from ?? "-"}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Paper>
          <Box sx={{ mt: 2, ml: 0.5, mb: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Salary Template Logs
            </Typography>
          </Box>
          <Box>
            <TableContainer ref={containerRef} sx={{ maxHeight: 450 }}>
              <Table className="border">
                <TableHead sx={stickyHeader}>
                  <TableRow sx={tableRowSize}>
                    {templateLogHeader?.map((value) => (
                      <TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templateData?.salary_template_logs?.length === 0 ? (
                    <InfoMessage
                      dataLength={templateData?.salary_template_logs}
                    />
                  ) : (
                    templateData?.salary_template_logs
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((value) => (
                        <TableRow sx={tableRowSize}>
                          {templateLogHeader.map((head) => (
                            <Tooltip
                              title="Revise History"
                              arrow
                              slotProps={tooltipStyleProps}
                            >
                              <TableCell
                                onClick={() => {
                                  setShowSalaryRevisionHistory(true);
                                  setGetRevisionId(value);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {value[head.key] ?? "N/A"}
                              </TableCell>
                            </Tooltip>
                          ))}
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
              {templateData?.salary_template_logs?.length > 10 && (
                <TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={templateData?.salary_template_logs?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </TableContainer>
          </Box>
        </Box>
        {showSalaryRevisionHistory && (
          <SalaryRevisionHistory
            showSalaryRevisionHistory={showSalaryRevisionHistory}
            setShowSalaryRevisionHistory={setShowSalaryRevisionHistory}
            getRevisionData={getRevisionId}
          />
        )}
      </Box>
    </>
  );
};
